import React, { useState } from 'react';
import { INTELLIGENCE, INTELLIGENCE_CARDS } from './phaseData';

/**
 * TodaysIntelligenceScreen — Phase A redesign.
 *
 * Scannable, card-driven answer to "What is my body doing today?"
 * Ordered as: Conclusion → Why → Experience → Action → Patterns →
 * Theo interpretation.  No walls of text — reading is optional.
 */
export default function TodaysIntelligenceScreen({ phase, onBack }) {
  const data  = INTELLIGENCE[phase]        || INTELLIGENCE.luteal;
  const cards = INTELLIGENCE_CARDS[phase]  || INTELLIGENCE_CARDS.luteal;
  const [actionOpen, setActionOpen] = useState(false);

  return (
    <div className="cx-subscreen" data-testid="screen-todays-intelligence">
      <header className="cx-sub-hd">
        <button className="cx-back-btn" onClick={onBack} aria-label="Back" data-testid="subscreen-back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="m15 6-6 6 6 6"/>
          </svg>
        </button>
        <div className="cx-sub-title">
          <span className="cx-sub-eyebrow">Today · Intelligence</span>
          What your body is doing
        </div>
      </header>

      <div className="cx-sub-body">

        {/* ── 1 · Conclusion ─────────────────────────────────────── */}
        <section className="cx-intel-hero">
          <div className="cx-intel-eyebrow">{data.hero.eyebrow}</div>
          <h1 className="cx-intel-h1" dangerouslySetInnerHTML={{ __html: data.hero.title }}/>
          <div className="cx-intel-conf-chip">
            <span className="cx-c-dot"/>Confidence · <b>{data.confidence}</b>
          </div>
        </section>

        {/* ── 2 · Why? — hormone story chips ─────────────────────── */}
        <section className="cx-intel-block">
          <div className="cx-intel-section-lbl">Why</div>
          <div className="cx-hormone-grid">
            {cards.hormones.map((h, i) => (
              <div key={i} className={'cx-hormone-cell cx-tone-' + h.tone} data-h={(h.name || '').toLowerCase()}>
                <div className="cx-hormone-head">
                  <span className="cx-hormone-name">{h.name}</span>
                  <TrendGlyph dir={h.dir}/>
                </div>
                <div className="cx-hormone-level">{h.level}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 3 · What you may experience ────────────────────────── */}
        <section className="cx-intel-block">
          <div className="cx-intel-section-lbl">What you may experience</div>
          <div className="cx-exp-grid">
            {cards.experience.map((e, i) => (
              <div key={i} className={'cx-exp-cell cx-tint-' + e.tint} data-k={(e.k || '').toLowerCase()}>
                <div className="cx-exp-k">{e.k}</div>
                <div className="cx-exp-v">{e.v}</div>
                <div className="cx-exp-r">{e.r}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 4 · What to do today — single action card ──────────── */}
        <section className="cx-intel-block">
          <div className="cx-intel-section-lbl">What to prioritize</div>
          <button
            className={'cx-action-card' + (actionOpen ? ' cx-open' : '')}
            onClick={() => setActionOpen(v => !v)}
            data-testid="action-card"
          >
            <div className="cx-action-head">
              <span className="cx-action-tag">{cards.action.tag}</span>
              <span className="cx-action-chev">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </span>
            </div>
            <div className="cx-action-title">{cards.action.title}</div>
            <div className="cx-action-science">{cards.action.science}</div>
          </button>
        </section>

        {/* ── 5 · Patterns detected ──────────────────────────────── */}
        <section className="cx-intel-block">
          <div className="cx-intel-section-lbl">Patterns Theo has noticed</div>
          <div className="cx-pattern-list">
            {cards.patterns.map((p, i) => (
              <div key={i} className="cx-pattern-card">
                <div className="cx-pattern-mark"/>
                <div className="cx-pattern-content">
                  <div className="cx-pattern-title">{p.title}</div>
                  <div className="cx-pattern-meta">
                    <span>Confidence · <b>{p.confidence}</b></span>
                    <span>·</span>
                    <span>{p.observed}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 6 · Theo's interpretation — the emotional highlight ── */}
        <section className="cx-ai-module" data-testid="ai-interpretation-module">
          <div className="cx-ai-module-hd">
            <div className="cx-ai-module-mark" aria-hidden="true"/>
            <div className="cx-ai-module-title">
              <div className="cx-eyebrow">Theo · daily interpretation</div>
              <div className="cx-name">Your body&apos;s story</div>
            </div>
          </div>
          <div className="cx-ai-module-body" dangerouslySetInnerHTML={{ __html: data.interpretation }}/>
        </section>
      </div>
    </div>
  );
}

function TrendGlyph({ dir }) {
  if (dir === 'up') return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7 17 17 7M9 7h8v8"/>
    </svg>
  );
  if (dir === 'down') return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7 7l10 10M17 9v8h-8"/>
    </svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 12h14"/>
    </svg>
  );
}
